export interface VastMedia {
  mediaUrl: string;
  durationSeconds: number;
  skippableAfterSeconds: number | null;
}

function first(element: Document | Element, localName: string): Element | null {
  return element.getElementsByTagNameNS('*', localName).item(0);
}

function parseDuration(value: string): number {
  const match = /^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/.exec(value.trim());
  if (!match) throw new Error('Invalid VAST duration.');
  const total = Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]);
  if (!Number.isFinite(total) || total < 1 || total > 120) {
    throw new Error('VAST duration is outside the supported limit.');
  }
  return total + (Number(match[4] || 0) >= 500 ? 1 : 0);
}

function parseSkip(value: string, duration: number): number | null {
  if (!value) return null;
  const seconds = value.endsWith('%')
    ? Math.round(duration * Number(value.slice(0, -1)) / 100)
    : parseDuration(value);
  if (!Number.isFinite(seconds) || seconds < 0 || seconds > duration) {
    throw new Error('Invalid VAST skip offset.');
  }
  return seconds;
}

export function parseVast(xml: string): VastMedia {
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.querySelector('parsererror') || document.documentElement.localName !== 'VAST') {
    throw new Error('Invalid VAST XML.');
  }
  const linear = first(document, 'Linear');
  const duration = parseDuration(first(linear || document, 'Duration')?.textContent || '');
  const mediaFiles = Array.from(document.getElementsByTagNameNS('*', 'MediaFile'))
    .map(element => ({
      type: (element.getAttribute('type') || '').toLowerCase(),
      width: Number(element.getAttribute('width') || 0),
      height: Number(element.getAttribute('height') || 0),
      url: (element.textContent || '').trim(),
    }))
    .filter(media => media.type === 'video/mp4' && media.url.startsWith('https://'))
    .filter(media => media.width > 0 && media.height > 0)
    .filter(media => Math.abs(media.width / media.height - 16 / 9) <= 0.03)
    .sort((left, right) => right.width * right.height - left.width * left.height);
  if (!mediaFiles.length) throw new Error('VAST has no compatible 16:9 MP4.');
  return {
    mediaUrl: mediaFiles[0].url,
    durationSeconds: duration,
    skippableAfterSeconds: parseSkip(linear?.getAttribute('skipoffset') || '', duration),
  };
}
